/* eslint-disable no-loop-func */
import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isoWeek from 'dayjs/plugin/isoWeek';
import {confetti} from "@tsparticles/confetti";


import styles from './opencalender.module.scss';

import 'dayjs/locale/en';
import KoIcon from '@kwapio/icons';
import {
  KoButton, KoDP, KoForm, KoFormBuilder,
  KoImage,
  KoModal, KoNano, KoSelect, KoText, KoTimeLine, KoTitle,
  KoToast
} from 'packages';
import { useParams } from 'react-router';
import { BrowseAPI } from '../browse.service';
import EngagementCalendar from './engagementCalendar';

dayjs.extend(weekday);
dayjs.extend(isoWeek);
const currentTime = dayjs().format('h:mma');
const CalendarPreview = () => {
  const [slotsObject, setSlots] = useState([]);
  const [slotBooked, setSlotBooked] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingModalVisible, setBookingModalVisible] = useState(null);
  const [, setLoading] = useState(false);
  const [eventData, setEventData] = useState();
  const [agendaModalVisible, setAgendaModalVisible] = useState(false);
  const [form] = KoForm.useForm();
  const { eventId, accountId, userId } = useParams();

  useEffect(() => {
    setLoading(true);
    BrowseAPI.getEventById(eventId, accountId).then(
      (data) => {
        setEventData(data?.event);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, []);



  const handleSlotClick = (date, slot) => {
    form.resetFields();
    setSelectedSlot({ date, ...slot });
    setBookingModalVisible(true);
  };

  const handleFinish = useCallback(values => {
    setLoading(true);
    if (!eventData?.bookingId) {
      values['bookingId'] = KoNano('BOK');
    }

    values['date'] =  dayjs.utc(selectedSlot.date, "DD MMM YYYY").startOf('day').toISOString();
    values['slot'] = `start:${selectedSlot.start} , end:${selectedSlot.end}`;
    setSlots(values);
    BrowseAPI.createOrUpdateEventsById(values, accountId, eventData.eventId, userId, eventData.bookingId)
      .then(() => {
        setLoading(false);
        // setSlotBooked(true);
        setBookingModalVisible(false);
        KoToast.success(`Your slot has been successfully booked for ${dayjs.utc(selectedSlot.date, "DD MMM YYYY").startOf('day').format('dddd, D MMM YYYY')}`);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          zIndex: 9900
        });

      }, () => setLoading(false));
  });

  const items = eventData?.agenda?.map(agenda => ({
    children: <KoText
      className={styles.lite}
      text={`"${agenda.title}" is a ${agenda.duration ?? 0}-minute session by ${agenda.presenter ?? 'Unknown'} (Status: ${agenda.status ?? 'N/A'}) scheduled on ${dayjs(agenda.dueDate).format('dddd, D MMM YYYY')}`}
    />

  }));
  const Email = ({ value, onChange }) => (
    <KoSelect
      value={value}
      showSearch
      onChange={v => onChange(v)}
      mode="tags"
      tokenSeparators={[',']}
      placeholder='Enter Email'
    />
  );

  const meta = {
    columns: 6,
    formItemLayout: null,
    colon: true,
    fields: [
      {
        key: 'name', label: 'Name', placeholder: 'Enter your name',
        rules: [{ required: true, message: 'Name is required' }], colSpan: 3
      },
      {
        key: 'email', label: 'Email', placeholder: 'Enter your email',
        rules: [{ required: true, message: 'Email is required' }], colSpan: 3
      },
      {
        key: 'guestEmails', label: 'Guest Email(s)', placeholder: 'Enter guest emails', widget: Email, colSpan: 6
      },
      {
        key: 'notes', label: 'Notes', widget: 'textarea', placeholder: 'Any prep materials?', colSpan: 6
      }
    ]
  };

  const iconMap = {
    zoom: { icon: 'ZoomFilled', text: 'Zoom video conference info added after booking' },
    teams: { icon: 'MicrosoftTeamsFilled', text: 'Microsoft Teams info added after booking' },
    gmeet: { icon: 'GoogleMeetFilled', text: 'Google Meet video conference info added after booking' }
  };

  const via = iconMap[eventData?.eventVia];

  const formatDuration=(duration)=> {
    if (duration && duration.endsWith('m')) {
      return `${parseInt(duration)} mins`;
    }
    if (duration && duration.endsWith('h')) {
      return `${parseInt(duration)} hour${parseInt(duration) > 1 ? 's' : ''}`;
    }
    return duration;
  };

  return (
    <div className={`${styles.container} kio-container`}>
      <div className={styles.wrapper}>
        {eventData?.ticketAmount > 0 && <div className={styles.pricetag}>
          <KoText
            className={styles.small}
            text={`${eventData?.ticketAmount === 0 ? 'Free' : `${eventData?.ticketAmount}`}`}
          />
        </div>}
        <div className={styles.leftWrapper}>
          <div className={styles.header}>
            {eventData?.logo ?  <div className={styles.logo}>
              <KoImage src={eventData?.logo[0]?.url} width={200} height={'auto'} />
            </div>:<div className={styles.logoFallback}></div>}
          </div>
          <div className={styles.title}>
            <div className={styles.logo}>
              <KoDP name={`${eventData?.organizerProfile?.firstName}`} shape={'circle'} logo={eventData?.organizerProfile?.logo} className={styles.profile} width={60} height={60} />
            </div>
            <div className={styles.titleTextAssignee}>
              <KoTitle text={`${eventData?.organizerProfile?.firstName} ${eventData?.organizerProfile?.lastName}` } level={5} />
            </div>
             
            <div className={styles.namedescription}>
              <KoTitle className={styles.titleText} text={eventData?.name || ''} ellipsis level={3} />
              {eventData?.description && <KoText className={styles.desc} text={eventData?.description || ''} />}
            </div>

            {via && (
              <div className={styles.meetingVia}>
                <KoIcon name={via.icon} width={18} height={18} />
                <KoText className={styles.small} text={via.text} />
              </div>
            )}

            <div className={styles.meetingVia}>
              <KoIcon name={'LogOutlined'} width={18} height={18} />
              <KoText className={styles.small} text={`${formatDuration(eventData?.duration)}`} />
            </div>

            {eventData?.agenda?.length > 0 &&  <div className={styles.meetingVia} onClick={() => setAgendaModalVisible(true)}>
              <KoIcon name={'InfoOutlined'} width={18} height={18} />
              <KoText className={styles.smallLink} text={'More info'} />
            </div>}
          </div>
          {/* <div className={styles.footerWrapper}>
            <KoButton className={styles.button} label={'Cookie settings'} type={'text'} />
            <KoButton className={styles.button} label={'Report abuse'} type={'link'}  />
          </div> */}
        </div>
        <div className={styles.calendarWrapper}>
          <KoTitle text='Schedule an Appointment' level={3} className={styles.title} />
          <KoText text='Select a date and time to book your appointment' className={styles.subTitle} />
          <div className={styles.calendar}>
            <EngagementCalendar handleSlotClick={handleSlotClick} meetings={[]}/>
          </div>
          <div className={`${styles.meetingVia} ${styles.timeZoneOutlined}`}>
            <KoIcon name={'TimeZoneOutlined'} width={16} height={16} />
            <KoText
              className={styles.small}
              text={`India Standard Time · ${currentTime}`}
            />
          </div>
          <div className={styles.brand} onClick={() => window.open('https://kwapio.com', '_blank')}>
            <KoText className={styles.title} text={'Powered by'} />
            <KoIcon name={'AppLogoFilled'} width={50} height={20} />
          </div>
        </div>
     
        <KoModal
          open={bookingModalVisible}
          onClose={() => setBookingModalVisible(false)}
          onCancel={() => setBookingModalVisible(false)}
          centered
          title={'Book an Appointment with ' + eventData?.name}
          footer={[
            <KoButton key="cancel" label="Close" onClick={() => {setBookingModalVisible(false); setSlotBooked(false);}} />,
            !slotBooked && <KoButton key="submit" color="primary" variant="solid" label="Book" onClick={() => form.submit()} />,
            slotBooked && <KoButton key="close" color="danger" variant="solid" label="Cancel your appointment" onClick={() => {setBookingModalVisible(false); setSlotBooked(false);}} />
          ]}
          width={700}
        >
          {!slotBooked && <div className={styles.attendeesInfo}>
            <div className={styles.attendeesHeader}>
              <div className={styles.eventInfo}>
                <div className={styles.eventInfoHeader}>
                  <KoDP name={`${eventData?.organizerProfile?.firstName} ${eventData?.organizerProfile?.lastName}`} shape={'square'} logo={eventData?.organizerProfile?.logo} className={styles.profile} width={30} height={30} />
                  <div className={styles.eventName}>
                    <KoTitle level={5} text={`${eventData?.organizerProfile?.firstName} ${eventData?.organizerProfile?.lastName}`} />
                    {/* <KoText className={styles.small} text={'Ceo, Kwapio'} /> */}
                    <KoText className={styles.small} text={
                      selectedSlot?.date && selectedSlot?.start && selectedSlot?.end
                        ? `${dayjs(selectedSlot.date).format('dddd, MMMM D, YYYY')} – ${dayjs(selectedSlot.start, 'HH:mm').format('h:mm A')} to ${dayjs(selectedSlot.end, 'HH:mm').format('h:mm A')}`
                        : ''
                    } />
                  </div>
                </div>

              </div>
              <KoTitle className={styles.attendeesTitle} level={5} text="Your contact info" />
            </div>
            <KoForm form={form} layout="vertical" onFinish={handleFinish}>
              <KoFormBuilder form={form} meta={meta} />
            </KoForm>
          </div>}
          {slotBooked && <div className={`${styles.attendeesInfo} ${styles.confirmedInfo}`}>
            <div className={styles.attendeesHeader2}>
              <KoIcon name={'TickFilled'} width={100} height={100} />
              <KoTitle className={styles.confirmed} level={4} text="Booking confirmed" />
              <KoText className={styles.info} text="Email sent to" />
              <KoText className={styles.info} text={`${slotsObject?.email}`}/>
            </div>
            <div className={styles.eventInfoHeader}>
              <KoDP name={`${eventData?.organizerProfile?.firstName} ${eventData?.organizerProfile?.lastName}`} shape={'square'} logo={eventData?.organizerProfile?.logo} className={styles.profile} width={30} height={30} />
              <div className={styles.eventName}>
                <KoTitle level={5} text={`${eventData?.organizerProfile?.firstName} ${eventData?.organizerProfile?.lastName}`} />
                <KoText className={styles.small} text={
                  selectedSlot?.date && selectedSlot?.start && selectedSlot?.end
                    ? `${dayjs(selectedSlot.date).format('dddd, MMMM D, YYYY')} – ${dayjs(selectedSlot.start, 'HH:mm').format('h:mm A')} to ${dayjs(selectedSlot.end, 'HH:mm').format('h:mm A')}`
                    : ''
                } />
              </div>
            </div>
            {via && (
              <div className={styles.meetingVia}>
                <KoIcon name={via.icon} width={16} height={16} />
                <KoText className={styles.small} text={via.text} />
              </div>
            )}
          </div>}
        </KoModal>

        <KoModal
          open={agendaModalVisible}
          onClose={() => setAgendaModalVisible(false)}
          onCancel={() => setAgendaModalVisible(false)}
          centered
          title={'Meeting Information & Agenda'}
          footer={[
            <KoButton key="cancel" label="Close" onClick={() => setAgendaModalVisible(false)} />
          ]}
          width={700}
        >
          <div>
            <KoText ellipsis={false} className={styles.desc} text={eventData?.description || ''} />
          </div>
          <div className={styles.agenda}>
            <KoTimeLine
              items={items}
            />

          </div>
        </KoModal>
      </div>
    </div>
  );
};

export default CalendarPreview;
